import { useEffect, useState } from 'react';
import { themeApi, type Theme, type ThemeListItem } from '../api';
import { linkProps } from '../router';
import { chooseTheme, currentTheme } from '../theme';

const SCOPE_LABEL = { builtin: 'Ingebouwd', shared: 'Voor iedereen', personal: 'Van jou' } as const;

/** Skin kiezen (per profiel, op elk apparaat) en naar de skin-studio. */
export function ThemePicker() {
  const [items, setItems] = useState<{ info: ThemeListItem; theme: Theme }[]>([]);
  const [current, setCurrent] = useState(currentTheme()?.id ?? '');

  useEffect(() => {
    themeApi.list()
      .then((r) => Promise.all(r.themes.map(async (info) => {
        const theme = await themeApi.get(info.id).catch(() => null);
        return theme ? { info, theme } : null;
      })))
      .then((list) => {
        const order = { personal: 0, shared: 1, builtin: 2 };
        setItems(list.filter((x): x is { info: ThemeListItem; theme: Theme } => !!x).sort((a, b) => order[a.info.scope] - order[b.info.scope]));
      })
      .catch(() => setItems([]));
  }, []);

  const choose = (t: Theme) => {
    void chooseTheme(t);
    setCurrent(t.id);
  };

  return (
    <section className="settings-block" aria-label="Skin">
      <h2>Skin</h2>
      <p className="subtle hint">Geldt voor dit profiel, op elk apparaat. Maak je eigen skin in de studio, of begin met een kopie van een bestaande.</p>
      <div className="theme-grid" role="radiogroup" aria-label="Skin" data-focus-group>
        {items.map(({ info, theme: t }) => {
          const c = t.tokens.color;
          return (
            <div key={t.id} className="theme-item">
              <button
                role="radio"
                aria-checked={current === t.id}
                className="theme-option"
                onClick={() => choose(t)}
                style={{ background: c.background, color: c.text, borderColor: current === t.id ? c.accent : c.border, borderRadius: t.tokens.radius.card }}
              >
                <span className="theme-swatches" aria-hidden="true">
                  <span style={{ background: c.surface }} />
                  <span style={{ background: c.surfaceRaised }} />
                  <span style={{ background: c.accent }} />
                </span>
                <span className="theme-name" style={{ fontFamily: t.tokens.font.display }}>{t.name}</span>
                <span className="theme-meta" style={{ color: c.textMuted }}>
                  {SCOPE_LABEL[info.scope]} · {t.layout.nav === 'left' ? 'menu links' : 'menu boven'}
                </span>
              </button>
              <a className="theme-edit" {...linkProps(`/settings/skin/${encodeURIComponent(t.id)}`)} aria-label={`${t.name} ${info.editable ? 'bewerken' : 'kopiëren en aanpassen'}`}>
                {info.editable ? 'Bewerken' : 'Aanpassen'}
              </a>
            </div>
          );
        })}
      </div>
      <a className="btn" {...linkProps('/settings/skin')}>Skin-studio openen</a>
    </section>
  );
}
